import React, {useEffect, useState} from "react";
import {Button, Col, Form, Input, Row, Spin, Steps} from "antd";
import * as Setting from "../../conf/Setting";
import * as Util from "../../utils/Util";
import i18next from "i18next";
import {CheckCircleFilled, IdcardOutlined, LockOutlined, MailOutlined, PhoneOutlined, ProfileFilled, UserOutlined, WechatOutlined} from "@ant-design/icons";
import {CountDownInput} from "../../components/common/CountDownInput";
import * as AuthBackend from "../../services/AuthBackend";
import * as ApplicationBackend from "../../services/ApplicationBackend";
import * as UserBackend from "../../services/UserBackend";
import {JSEncrypt} from "jsencrypt";
import "../../assets/styles/auth/AccountActivatePage.less";
import * as PasswordPolicyBackend from "../../services/PasswordPolicyBackend";
import {Prompt} from "react-router-dom";
import GlobalContext from "../../context/GlobalContext";
import * as AccountActivationBackend from "../../services/AccountActivationBackend";

const {Step} = Steps;

const WeChat = {
  scope: "snsapi_login",
  mpScope: "snsapi_userinfo",
};

const activationProcessIcon = {
  userVerify: <ProfileFilled />,
  WeChat: <WechatOutlined />,
  WeCom: <WechatOutlined />,
  email: <MailOutlined />,
  phone: <PhoneOutlined />,
  password: <LockOutlined />,
};

export const AccountActivatePage = (props) => {
  const [msg] = useState(null);
  const [currentStepName, setCurrentStepName] = useState("None");
  const [application, setApplication] = useState(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pwdPolicy, setPwdPolicy] = useState(null);
  const [loadingWeChat, setLoadingWeChat] = useState(false);
  const [loginUser, setLoginUser] = useState(null);
  const [activateConfig, setActivateConfig] = useState(null);
  const [loadingNext, setLoadingNext] = useState(false);

  const {account} = props;

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    (async function fn() {
      const applicationId = getApplicationId();
      if (applicationId === undefined) {
        Util.showMessage(
          "error",
          i18next.t("activate:Application config error")
        );
        return;
      }

      let applicationRes, actConfig;
      try {
        applicationRes = await ApplicationBackend.getApplication(Setting.Admin, applicationId);
        setApplication(applicationRes);

        actConfig = await AccountActivationBackend.getConfig(Setting.Admin, applicationRes.organizationObj.acctActConf);
        if (Setting.isMobile()) {
          // 移动端不展示微信和企业微信的步骤
          actConfig.flow = actConfig.flow.map(item => JSON.parse(item)).filter(item => item.value !== "WeChat" && item.value !== "WeCom");
        } else {
          actConfig.flow = actConfig.flow.map(item => JSON.parse(item));
        }
        setActivateConfig(actConfig);
      } catch (error) {
        Setting.showResponseMessage(error.message);
      }

      if (account === null || account === undefined || applicationRes === undefined || actConfig === undefined) {
        goNext(currentStepName, actConfig, applicationRes);
        return;
      }

      PasswordPolicyBackend.getConfig(Setting.Admin, applicationRes.organizationObj.pwdConfig)
        .then(resConfig => {
          if (resConfig.msg === undefined) {
            setPwdPolicy(resConfig);
          } else {
            Setting.showResponseMessage(resConfig.msg);
          }
        });

      const params = new URLSearchParams(window.location.search);
      const code = params.get("code"); // 微信code
      if (!code) {
        goNext(currentStepName, actConfig, applicationRes, account);
        return;
      }

      const innerParams = getInnerParams();
      const providerId = innerParams.get("provider");
      const providerType = innerParams.get("providertype");
      const method = innerParams.get("method");
      const samlRequest = innerParams.get("SAMLRequest");
      if (providerType === "WeChat") {
        // 微信显示二维码
        setCurrentStepName("WeChat");
        setLoadingWeChat(true);

        if (account.wechat !== undefined && account.wechat !== "") {
          setLoadingWeChat(false);
          goNext(currentStepName, actConfig, applicationRes, account);
          return;
        }
      }

      if (providerType === "WeCom") {
        // 企业微信显示二维码
        setCurrentStepName("WeCom");
        setLoadingWeChat(true);

        if (account.wecom !== undefined && account.wecom !== "") {
          setLoadingWeChat(false);
          goNext(currentStepName, actConfig, applicationRes, account);
          return;
        }
      }

      if (providerType !== "WeCom" && providerType !== "WeChat") {
        return;
      }

      const body = {
        type: "link",
        application: applicationId,
        provider: providerId,
        code: code,
        samlRequest: samlRequest,
        state: applicationId,
        redirectUri: `${window.location.origin}${window.location.pathname}`,
        method: method,
      };
      // const oAuthParams = Util.getOAuthGetParameters(innerParams);
      const oAuthParams = {
        clientId: applicationRes.clientId,
        responseType: "",
        redirectUri: body.redirectUri,
        scope: "read",
        state: "casdoor",
      };
      AuthBackend.login(body, oAuthParams)
        .then((res) => {
          if (res.status === "ok") {
            setLoadingNext(false);
            AuthBackend.getAccount("")
              .then((resAccount) => {
                if (resAccount.status === "ok") {
                  setLoadingWeChat(false);
                  setLoginUser(resAccount.data);
                  if (providerType === "WeChat") {
                    goNext("WeChat", actConfig, applicationRes, resAccount.data);
                  } else if (providerType === "WeCom") {
                    goNext("WeCom", actConfig, applicationRes, resAccount.data);
                  }
                }
              });
          } else {
            Setting.showResponseMessage(res.msg);
            setTimeout(() => {
              window.location.href = `${window.location.origin}${window.location.pathname}?login_url=${params.get("login_url")}`;
            }, 1000);
          }
        });
    })();
  }, []);

  useEffect(() => {
    if (account !== undefined && account !== null) {
      setLoginUser(account);
    }
  }, [account]);

  const blockTransport = async(location, updateAccount) => {
    if (!isOpen) {
      // const leave = window.confirm("您确定要离开该页面吗?");
      // if (!leave) {
      //   return false;
      // }
      if (location.pathname.startsWith("/login") && (loginUser || account)) {
        await AuthBackend.logout();
        updateAccount(null);
      }
      return true;
    } else {
      setIsOpen(false);
      // 返回false是拦截的关键
      return false;
    }
  };

  const getStepIndex = (curStepName, actConfig) => {
    if (!actConfig) {
      return -1;
    }

    if (curStepName === "None") {
      return -1;
    }

    if (curStepName === "final") {
      return actConfig.flow.length;
    }

    const step = actConfig.flow.findIndex(item => item.value === curStepName);
    return step;
  };

  const getStep = (curStepName, actConfig) => {
    if (!actConfig) {
      return null;
    }

    if (curStepName === "None") {
      return null;
    }

    if (curStepName === "final") {
      return null;
    }

    const step = actConfig.flow.find(item => item.value === curStepName);
    return step;
  };

  const getNextStepName = (curStepName, actConfig, accountUser = null) => {
    if (!actConfig) {
      return "None";
    }

    let name = "password";
    if (curStepName === "None" && accountUser) {
      // 已经登录的情况下，刷新页面会进入这个条件
      for (let index = 0; index < actConfig.flow.length; index++) {
        if (actConfig.flow[index].value === "WeChat" && !accountUser.wechat) {
          name = actConfig.flow[index].value;
          break;
        }
        if (actConfig.flow[index].value === "WeCom" && !accountUser.wecom) {
          name = actConfig.flow[index].value;
          break;
        }
        if (actConfig.flow[index].value === "email" && !accountUser.email) {
          name = actConfig.flow[index].value;
          break;
        }
        if (actConfig.flow[index].value === "phone" && !accountUser.phone) {
          name = actConfig.flow[index].value;
          break;
        }
      }

      return name;
    }

    if (curStepName === "None") {
      return "userVerify";
    }

    const step = getStepIndex(curStepName, actConfig);

    if (step === actConfig.flow.length - 1) {
      return "final";
    }

    const nextStep = actConfig.flow.find((item, index) => index === step + 1);
    return nextStep.value;
  };

  const goNext = (curStepName, actConfig, applicationObj, accountUser = null) => {

    const nextStepName = getNextStepName(curStepName, actConfig, accountUser);
    if (nextStepName === "WeChat") {
      if (accountUser.wechat !== undefined && accountUser.wechat !== "") {
        // 微信已绑定的情况
        goNext("WeChat", actConfig, applicationObj, accountUser);
        return;
      }

      setCurrentStepName(nextStepName);
      const wechatConfig = applicationObj.providers.find(item => {
        return item.provider.type === "WeChat";
      });
      if (!wechatConfig) {
        Setting.showMessage("error", i18next.t("activate:No wechat provider"));
      }
      setLoadingWeChat(true);
      setTimeout(() => {
        setLoadingWeChat(false);
        loadWeChatQRCode(wechatConfig, applicationObj);
      }, 500);

      return;
    }

    if (nextStepName === "WeCom") {
      if (accountUser.wecom !== undefined && accountUser.wecom !== "") {
        // 企业微信已绑定的情况
        goNext("WeCom", actConfig, applicationObj, accountUser);
        return;
      }

      setCurrentStepName(nextStepName);
      const weComConfig = applicationObj.providers.find(item => {
        return item.provider.type === "WeCom";
      });
      if (!weComConfig) {
        Setting.showMessage("error", i18next.t("没有企业微信身份源"));
      }
      setLoadingWeChat(true);
      setTimeout(() => {
        setLoadingWeChat(false);
        loadWeComQRCode(weComConfig, applicationObj);
      }, 500);

      return;
    }

    if (nextStepName === "email" && accountUser.email !== undefined && accountUser.email !== "") {
      // 邮箱已绑定的情况
      goNext("email", actConfig, applicationObj, accountUser);
      return;
    }

    if (nextStepName === "phone" && accountUser.phone !== undefined && accountUser.phone !== "") {
      // 手机号已绑定的情况
      goNext("phone", actConfig, applicationObj, accountUser);
      return;
    }

    setCurrentStepName(nextStepName);
  };

  const getApplicationId = () => {
    if (props.match !== undefined && props.match.params.applicationId !== undefined) {
      const arr = props.match.params.applicationId.split("?");
      const pathname = arr.length > 0 ? arr[0] : undefined;
      return pathname;
    }
    if (props.applicationId !== undefined) {
      return props.applicationId;
    }
    return undefined;
  };

  const getApplicationObj = () => {
    if (props.application !== undefined) {
      return props.application;
    } else {
      return application;
    }
  };

  const getInnerParams = () => {
    // For example, for Casbin-OA, realRedirectUri = "http://localhost:9000/login"
    // realRedirectUrl = "http://localhost:9000"
    const params = new URLSearchParams(location.search);
    const state = params.get("state");
    const queryString = Util.getQueryParamsFromState(state);
    return new URLSearchParams(queryString);
  };

  // 加载微信二维码
  const loadWeChatQRCode = (wechatConfig, applicationRes) => {
    if (!wechatConfig || !applicationRes) {
      return;
    }

    // 这里的处理是为了避免企业微信或者微信已经扫码重定向回来携带的信息再次编码进state，只保留login_url
    const isShortState = navigator.userAgent.includes("MicroMessenger");
    const params = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams({"login_url": params.get("login_url")});
    const query = `?${newParams.toString()}`;
    const state = Util.getStateFromQueryParamsForWechat(query, applicationRes.uid, wechatConfig.uid, "link", isShortState, wechatConfig.provider.type);
    const href = `${window.location.origin}${window.location.pathname}${query}`;
    new window.WxLogin({
      self_redirect: false,
      id: "wechat_bind",
      appid: wechatConfig.provider.clientId,
      scope: navigator.userAgent.includes("MicroMessenger") ? WeChat.mpScope : WeChat.scope,
      redirect_uri: encodeURI(href),
      state: state,
      style: "",
      href: "",
    });
  };

  // 加载企业微信二维码
  const loadWeComQRCode = (weComConfig, applicationRes) => {
    if (!weComConfig || !applicationRes) {
      return;
    }

    // 这里的处理是为了避免企业微信或者微信已经扫码重定向回来携带的信息再次编码进state，只保留login_url
    const params = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams({"login_url": params.get("login_url")});
    const query = `?${newParams.toString()}`;
    const state = Util.getStateFromQueryParamsForWechat(query, applicationRes.uid, weComConfig.uid, "link", false, weComConfig.provider.type);
    const href = `${window.location.origin}${window.location.pathname}${query}`;
    // eslint-disable-next-line no-undef
    new WwLogin({
      id: "ww_bind",
      appid: weComConfig.provider.clientId,
      agentid: weComConfig.provider.appId,
      redirect_uri: encodeURI(href),
      state: state,
      href: "",
      lang: "zh",
    });
  };

  const onFormFinish = (name, info, forms) => {
    switch (name) {
    case "stepUserVerify": // 信息校验
      const userName = forms.stepUserVerify.getFieldValue("username");
      const oAuthParams = Util.getOAuthGetParameters();

      setLoadingNext(true);
      AuthBackend.login({
        application: forms.stepUserVerify.getFieldValue("application"),
        organization: forms.stepUserVerify.getFieldValue("organization"),
        username: userName,
        name: forms.stepUserVerify.getFieldValue("name"),
        type: "login",
      }, oAuthParams).then((res) => {
        if (res.status === "ok") {
          PasswordPolicyBackend.getConfig(Setting.Admin, applicationObj.organizationObj.pwdConfig)
            .then(resConfig => {
              if (resConfig.msg === undefined) {
                setPwdPolicy(resConfig);
              } else {
                Setting.showResponseMessage(resConfig.msg);
              }
            });

          AuthBackend.getAccount("")
            .then((resAccount) => {
              setLoadingNext(false);
              if (resAccount.status === "ok") {
                setLoginUser(resAccount.data);
                goNext(currentStepName, activateConfig, application, resAccount.data);
              }
            });
        } else {
          setLoadingNext(false);
          Setting.showResponseMessage(res.msg);
        }
      });
      break;
    case "stepPassword": // 设置密码
      const newPassword = forms.stepPassword.getFieldValue("newPassword");

      setLoadingNext(true);
      AuthBackend.getPublicKey().then(resPubKey => {
        if (resPubKey.status === "ok") {
          const encrypt = new JSEncrypt();
          encrypt.setPublicKey(resPubKey.msg);
          const newEncryptPassword = encrypt.encrypt(newPassword);
          const applicationObj = getApplicationObj();

          PasswordPolicyBackend.checkPassword(applicationObj.organizationObj.owner, applicationObj.organizationObj.uid, applicationObj.organizationObj.uid, loginUser.name, newEncryptPassword)
            .then(resCheck => {
              if (resCheck.status === "ok") {
                UserBackend.setPassword(applicationObj?.organizationObj.uid, loginUser.name, "", newEncryptPassword).then(res => {
                  if (res.status === "ok") {
                    UserBackend.activateUser(loginUser.name, applicationObj?.organizationObj.uid).then(resUser => {
                      if (resUser.status === "ok") {
                        AuthBackend.logout().then(resLogout => {
                          setLoadingNext(false);
                          Setting.showMessage("success", i18next.t("activate:User activated successfully"));
                          goNext(currentStepName, activateConfig, application, loginUser);
                        });
                      } else {
                        setLoadingNext(false);
                        Setting.showResponseMessage(resUser.msg);
                      }
                    }).catch(error => {
                      setLoadingNext(false);
                      Setting.showResponseMessage(error);
                    });
                  } else {
                    setLoadingNext(false);
                    Setting.showResponseMessage(res.msg);
                  }
                });
              } else {
                setLoadingNext(false);
                Setting.showResponseMessage(resCheck.msg);
              }
            });
        } else {
          setLoadingNext(false);
          Setting.showResponseMessage(resPubKey.msg);
        }
      });
      break;
    case "stepEmail": // 绑定邮箱
      const email = forms.stepEmail.getFieldValue("email");
      const emailCode = forms.stepEmail.getFieldValue("emailCode");
      setLoadingNext(true);
      UserBackend.resetEmailOrPhone(email, "email", emailCode).then(res => {
        setLoadingNext(false);
        if (res.status === "ok") {
          Setting.showMessage("success", i18next.t("user:Set email successfully"));
          goNext(currentStepName, activateConfig, application, loginUser);
        } else {
          Setting.showResponseMessage(res.msg);
        }
      }).catch(error => {
        setLoadingNext(false);
        Setting.showResponseMessage(error);
      });
      break;
    case "stepPhone": // 绑定手机号
      const phone = forms.stepPhone.getFieldValue("phone");
      const phoneCode = forms.stepPhone.getFieldValue("phoneCode");
      setLoadingNext(true);
      UserBackend.resetEmailOrPhone(phone, "phone", phoneCode).then(res => {
        setLoadingNext(false);
        if (res.status === "ok") {
          Setting.showMessage("success", i18next.t("user:Set phone successfully"));
          goNext(currentStepName, activateConfig, application, loginUser);
        } else {
          Setting.showResponseMessage(res.msg);
        }
      }).catch(error => {
        setLoadingNext(false);
        Setting.showResponseMessage(error);
      });
      break;
    default:
      break;
    }
  };

  const onFinishFailed = (values, errorFields) => {};

  const renderForm = (applicationObj) => {
    const formMarginBottom = Setting.isMobile() ? {marginBottom: 16} : {};
    const invisibleFormMarginBottom = Setting.isMobile() ? {marginBottom: 10} : {};
    return (
      <React.Fragment>
        <Form.Provider onFormFinish={(name, {info, forms}) => {
          onFormFinish(name, info, forms);
        }}>
          {/* STEP UserVerify: 信息校验 */}
          <Form
            hidden={currentStepName !== "userVerify"}
            name="stepUserVerify"
            onFinishFailed={(errorInfo) =>
              onFinishFailed(
                errorInfo.values,
                errorInfo.errorFields,
                errorInfo.outOfDate
              )
            }
            initialValues={{
              application: applicationObj.uid,
              organization: applicationObj.organization,
            }}
            style={Setting.isMobile() ? {} : {width: "450px"}}
            size={Setting.isMobile() ? "default" : "large"}
            labelCol={{span: 4}}
            wrapperCol={{span: 18}}
          >
            <Form.Item
              style={{height: 0, visibility: "hidden", ...invisibleFormMarginBottom}}
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "forget:Please input your application!"
                  ),
                },
              ]}
            />
            <Form.Item
              style={{height: 0, visibility: "hidden", ...invisibleFormMarginBottom}}
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "forget:Please input your organization!"
                  ),
                },
              ]}
            />
            <Form.Item
              style={formMarginBottom}
              label={Setting.isMobile() ? "" : i18next.t("activate:Username")}
              name="username"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "activate:Please input your username"
                  ),
                  whitespace: true,
                },
                {
                  validator: (_, value) => {
                    if (value === undefined) {
                      return Promise.resolve();
                    }
                    if (value.length > 50) {
                      return Promise.reject(
                        i18next.t("general:Limited number of characters is {number}").replace("{number}", 50)
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                onChange={(e) => {
                }}
                prefix={<UserOutlined />}
                placeholder={i18next.t("activate:Please input your username")}
              />
            </Form.Item>
            <Form.Item
              style={formMarginBottom}
              label={Setting.isMobile() ? "" : i18next.t("user:Display name")}
              name="name"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "activate:Please input your name"
                  ),
                  whitespace: true,
                },
                {
                  validator: (_, value) => {
                    if (value === undefined) {
                      return Promise.resolve();
                    }
                    if (value.length > 50) {
                      return Promise.reject(
                        i18next.t("general:Limited number of characters is {number}").replace("{number}", 50)
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                onChange={(e) => {
                }}
                prefix={<IdcardOutlined />}
                placeholder={i18next.t("activate:Please input your name")}
              />
            </Form.Item>
            <br />
            <Form.Item {...{wrapperCol: {span: 18, offset: Setting.isMobile() ? 0 : 4}}}>
              <Button block type="primary" htmlType="submit" loading={loadingNext}>
                {i18next.t("forget:Next Step")}
              </Button>
            </Form.Item>
          </Form>

          {/* STEP Password：设置密码 */}
          <Form
            hidden={currentStepName !== "password"}
            name="stepPassword"
            onFinishFailed={(errorInfo) =>
              onFinishFailed(
                errorInfo.values,
                errorInfo.errorFields,
                errorInfo.outOfDate
              )
            }
            initialValues={{
              application: applicationObj.uid,
              organization: applicationObj.organization,
            }}
            style={Setting.isMobile() ? {} : {width: "500px"}}
            size="large"
          >
            <Form.Item
              style={Setting.isMobile() ? {height: 0, visibility: "hidden", ...invisibleFormMarginBottom}
                : {height: 0, visibility: "hidden"}}
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "forget:Please input your application!"
                  ),
                },
              ]}
            />
            <Form.Item
              style={Setting.isMobile() ? {height: 0, visibility: "hidden", ...invisibleFormMarginBottom}
                : {height: 0, visibility: "hidden"}}
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "forget:Please input your organization!"
                  ),
                },
              ]}
            />
            <Form.Item
              label={Setting.isMobile() ? "" : i18next.t("activate:Set Password")}
              name="newPassword"
              style={Setting.isMobile() ? {...invisibleFormMarginBottom} : {marginBottom: 12}}
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "activate:Please input your password"
                  ),
                },
              ]}
              hasFeedback
            >
              <Input.Password
                disabled={false}
                prefix={<LockOutlined />}
                placeholder={i18next.t("activate:Please input new password")}
              />
            </Form.Item>
            <Row>
              <Col offset={4}>
                <div style={{fontSize: Setting.isMobile() ? 12 : 14, color: "#999999", textAlign: "left", marginBottom: 12}}>
                  {Setting.getPasswordRuleText(pwdPolicy)}
                </div>
              </Col>
            </Row>
            <br />
            <Form.Item>
              <Button block type="primary" htmlType="submit" disabled={false} loading={loadingNext}>
                {i18next.t("forget:Next Step")}
              </Button>
            </Form.Item>
            <div style={{marginBottom: 20}}>{i18next.t("activate:Bind tip2")}</div>
          </Form>

          {/* STEP Email: 绑定邮箱 */}
          <Form
            hidden={currentStepName !== "email"}
            name="stepEmail"
            onFinishFailed={(errorInfo) =>
              onFinishFailed(
                errorInfo.values,
                errorInfo.errorFields,
                errorInfo.outOfDate
              )
            }
            initialValues={{
              application: applicationObj.uid,
              organization: applicationObj.organization,
            }}
            style={Setting.isMobile() ? {} : {width: "450px"}}
            size="large"
            labelCol={{span: 4}}
          >
            <Form.Item
              style={Setting.isMobile() ? {height: 0, visibility: "hidden", ...invisibleFormMarginBottom}
                : {height: 0, visibility: "hidden"}}
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "forget:Please input your application!"
                  ),
                },
              ]}
            />
            <Form.Item
              style={Setting.isMobile() ? {height: 0, visibility: "hidden", ...invisibleFormMarginBottom}
                : {height: 0, visibility: "hidden"}}
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "forget:Please input your organization!"
                  ),
                },
              ]}
            />
            <Form.Item
              label={Setting.isMobile() ? "" : i18next.t("general:Mail")}
              name="email"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "signup:Please input your Email!"
                  ),
                  whitespace: true,
                },
                {
                  validator: (_, value) => {
                    if (value === undefined) {
                      return Promise.resolve();
                    }
                    if (!Setting.isValidEmail(value)) {
                      return Promise.reject(
                        i18next.t("login:The input is not valid Email!")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                prefix={<MailOutlined />}
                placeholder={i18next.t("signup:Please input your Email!")}
              />
            </Form.Item>
            <Form.Item
              label={Setting.isMobile() ? "" : i18next.t("login:Verification Code")}
              name="emailCode" // use emailCode instead of email/emailCode to adapt to RequestForm in account.go
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "code:Please input your verification code!"
                  ),
                },
                {
                  validator: (_, value) => {
                    if (value === undefined) {
                      return Promise.resolve();
                    }
                    if (value.length > 6) {
                      return Promise.reject(
                        i18next.t("general:Limited number of characters is {number}").replace("{number}", 6)
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              style={Setting.isMobile() ? {marginBottom: 12} : {}}
            >
              <CountDownInput
                from="activate"
                disabled={email === ""}
                onButtonClickArgs={[email, "email", Setting.getApplicationName(applicationObj)]}
                application={applicationObj}
              />
            </Form.Item>
            <br />
            {
              getStep(currentStepName, activateConfig)?.enableSkip ?
                <Form.Item>
                  <Row justify="space-between">
                    <Col span={10}>
                      <Button ghost type="primary" disabled={false} style={{marginBottom: 0, width: "100%"}} onClick={() => {
                        goNext(currentStepName, activateConfig, application, loginUser);
                      }}>
                        {i18next.t("activate:Skip")}
                      </Button>
                    </Col>
                    <Col span={10}>
                      <Button
                        block
                        type="primary"
                        htmlType="submit"
                        loading={loadingNext}
                      >
                        {i18next.t("forget:Next Step")}
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
                :
                <Form.Item>
                  <Row justify="space-between">
                    <Col span={24}>
                      <Button
                        block
                        type="primary"
                        htmlType="submit"
                        loading={loadingNext}
                      >
                        {i18next.t("forget:Next Step")}
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
            }
            <div style={{marginBottom: 20}}>{i18next.t("activate:Bind tip1")}</div>
          </Form>

          {/* STEP Phone: 绑定手机号 */}
          <Form
            hidden={currentStepName !== "phone"}
            name="stepPhone"
            onFinishFailed={(errorInfo) =>
              onFinishFailed(
                errorInfo.values,
                errorInfo.errorFields,
                errorInfo.outOfDate
              )
            }
            initialValues={{
              application: applicationObj.uid,
              organization: applicationObj.organization,
            }}
            style={Setting.isMobile() ? {} : {width: "450px"}}
            size="large"
            labelCol={{span: 4}}
          >
            <Form.Item
              style={Setting.isMobile() ? {height: 0, visibility: "hidden", ...invisibleFormMarginBottom}
                : {height: 0, visibility: "hidden"}}
              name="application"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "forget:Please input your application!"
                  ),
                },
              ]}
            />
            <Form.Item
              style={Setting.isMobile() ? {height: 0, visibility: "hidden", ...invisibleFormMarginBottom}
                : {height: 0, visibility: "hidden"}}
              name="organization"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "forget:Please input your organization!"
                  ),
                },
              ]}
            />
            <Form.Item
              label={Setting.isMobile() ? "" : i18next.t("general:Phone")}
              name="phone"
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "signup:Please input your phone number!"
                  ),
                  whitespace: true,
                },
                {
                  validator: (_, value) => {
                    if (value === undefined) {
                      return Promise.resolve();
                    }
                    if (!Setting.isValidPhone(value)) {
                      return Promise.reject(
                        i18next.t("login:The input is not valid Phone!")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                onChange={(e) => {
                  setPhone(e.target.value);
                }}
                prefix={<MailOutlined />}
                placeholder={i18next.t("signup:Please input your phone number!")}
              />
            </Form.Item>
            <Form.Item
              label={Setting.isMobile() ? "" : i18next.t("login:Verification Code")}
              name="phoneCode" // use emailCode instead of email/emailCode to adapt to RequestForm in account.go
              rules={[
                {
                  required: true,
                  message: i18next.t(
                    "code:Please input your verification code!"
                  ),
                },
                {
                  validator: (_, value) => {
                    if (value === undefined) {
                      return Promise.resolve();
                    }
                    if (value.length > 6) {
                      return Promise.reject(
                        i18next.t("general:Limited number of characters is {number}").replace("{number}", 6)
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              style={Setting.isMobile() ? {marginBottom: 12} : {}}
            >
              <CountDownInput
                from="activate"
                disabled={phone === ""}
                onButtonClickArgs={[phone, "phone", Setting.getApplicationName(applicationObj)]}
                application={applicationObj}
              />
            </Form.Item>
            <br />
            {
              getStep(currentStepName, activateConfig)?.enableSkip ?
                <Form.Item>
                  <Row justify="space-between">
                    <Col span={10}>
                      <Button ghost type="primary" disabled={false} style={{marginBottom: 0, width: "100%"}} onClick={() => {
                        goNext(currentStepName, activateConfig, application, loginUser);
                      }}>
                        {i18next.t("activate:Skip")}
                      </Button>
                    </Col>
                    <Col span={10}>
                      <Button
                        block
                        type="primary"
                        htmlType="submit"
                        loading={loadingNext}
                      >
                        {i18next.t("forget:Next Step")}
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
                :
                <Form.Item>
                  <Row justify="space-between">
                    <Col span={24}>
                      <Button
                        block
                        type="primary"
                        htmlType="submit"
                        loading={loadingNext}
                      >
                        {i18next.t("forget:Next Step")}
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
            }
            <div style={{marginBottom: 20}}>{i18next.t("activate:Bind tip1")}</div>
          </Form>
        </Form.Provider>

        <Row style={{width: Setting.isMobile() ? "100%" : "450px"}} hidden={currentStepName !== "WeChat"}>
          <Col span={24} style={{marginTop: 20}}>
            {
              loadingWeChat &&
              <div style={{width: 406, height: 406, display: "flex", alignItems: "center", justifyContent: "center"}}>
                <Spin size="large" tip={i18next.t("activate:WeChat Authorizing")} />
              </div>
            }
            <div id="wechat_bind" ></div>
            {
              getStep(currentStepName, activateConfig)?.enableSkip ?
                <Button ghost type="primary" disabled={false} style={{marginBottom: 12, width: "100%", height: 40}} onClick={() => {
                  goNext(currentStepName, activateConfig, application, loginUser);
                }}>
                  {i18next.t("activate:Skip")}
                </Button>
                :
                null
            }
            <div style={{marginBottom: 20}}>{i18next.t("activate:Bind tip1")}</div>
          </Col>
        </Row>
        <Row style={{width: Setting.isMobile() ? "100%" : "450px"}} hidden={currentStepName !== "WeCom"}>
          <Col span={24} style={{marginTop: 20}}>
            {
              loadingWeChat &&
              <div style={{width: 406, height: 406, display: "flex", alignItems: "center", justifyContent: "center"}}>
                <Spin size="large" tip={i18next.t("activate:WeCom Authorizing")} />
              </div>
            }
            <div id="ww_bind" ></div>
            {
              getStep(currentStepName, activateConfig)?.enableSkip ?
                <Button ghost type="primary" disabled={false} style={{marginBottom: 12, width: "100%", height: 40}} onClick={() => {
                  goNext(currentStepName, activateConfig, application, loginUser);
                }}>
                  {i18next.t("activate:Skip")}
                </Button>
                :
                null
            }
            <div style={{marginBottom: 20}}>{i18next.t("activate:Bind tip1")}</div>
          </Col>
        </Row>
        <Row style={{width: Setting.isMobile() ? "100%" : "450px"}} hidden={currentStepName !== "final"}>
          <Col span={24} style={{marginTop: 60}}>
            <CheckCircleFilled style={{fontSize: 48, color: "#81D564"}} />
            <div style={{marginTop: 10}}>{i18next.t("activate:Congrats, your account has activated succesfully")}</div>
            <Col offset={4} span={16}>
              <Button style={{marginTop: Setting.isMobile() ? 50 : 80}} block type="primary" onClick={() => {
                const params = new URLSearchParams(window.location.search);
                const loginUrl = Util.decodeUrl(params.get("login_url"));
                if (loginUrl) {
                  Setting.goToLink(loginUrl);
                } else {
                  Setting.goToLink("/");
                }
              }}>{i18next.t("activate:Sign in application")}</Button>
            </Col>
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  const applicationObj = getApplicationObj();
  if (applicationObj === null) {
    return Util.renderMessageLarge(this, Setting.getResponseMessage(msg), () => {
      const params = new URLSearchParams(window.location.search);
      const loginUrl = Util.decodeUrl(params.get("login_url"));
      if (loginUrl) {
        Setting.goToLink(loginUrl);
      } else {
        Setting.goToLink("/");
      }
    });
  }

  const antCol24Mobile = {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      height: "100%",
      background: `url(${
        require("../../assets/images/building.png").default
      }) center top no-repeat`,
      position: "relative",
    },
    antCol24DivMobile = {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      height: 450,
      width: "84%",
      boxSizing: "border-box",
      padding: "0 0",
      backgroundColor: "rgba(255, 255, 255, 1)",
      borderRadius: 8,
      boxShadow: "4px 4px 24px 0px rgba(54, 85, 141, 0.1)",
      position: "relative",
    },
    styleDisplaynameMobile = {
      color: "#46494C",
      textAlign: "center",
      fontSize: 19,
      fontWeight: "bold",
      margin: 0,
    };
  return (
    <GlobalContext.Consumer>
      {({account, updateAccount}) => (
        <>
          <Prompt
            when={true}  // 一个状态when = true 是才触发下面方法
            message={(location) => blockTransport(location, updateAccount)}
          />
          {Setting.isMobile() ?
            <Row style={{height: "100vh", width: "100vw",
              background: "linear-gradient(to bottom, #349BFB, 40% , #ffffff)"}}>
              <Col span={24} style={antCol24Mobile}>
                <div style={antCol24DivMobile}>
                  <div style={{width: "100%", marginTop: 63, marginBottom: 34}}>
                    <p style={styleDisplaynameMobile}>{i18next.t("activate:Account Activate")}</p>
                  </div>
                  <Row style={{height: 60}}>
                    <Col span={24} style={{height: "100%"}}>
                      {
                        !activateConfig ? null :
                          <Steps
                            className="account-activate-steps"
                            current={getStepIndex(currentStepName, activateConfig)}
                            labelPlacement="vertical"
                            responsive={false}
                            size="small"
                            style={{
                              width: "100%",
                              // maxWidth: "300px",
                              // margin: "auto",
                              height: "100%",
                            }}
                          >
                            {
                              activateConfig.flow.map(flowStep => {
                                return <Step key={flowStep.value}
                                  title={Setting.activationProcess[flowStep.value].labelText}
                                  icon={activationProcessIcon[flowStep.value]}
                                />;
                              })
                            }
                            <Step
                              title={i18next.t("activate:Complete")}
                              icon={<CheckCircleFilled />}
                            />
                          </Steps>
                      }

                    </Col>
                  </Row>
                  <div style={{marginTop: "10px", textAlign: "center", paddingLeft: 26, paddingRight: 26}}>
                    {renderForm(applicationObj)}
                  </div>
                </div>
              </Col>
            </Row>
            :
            <Row>
              <Col span={24} style={{justifyContent: "center", marginTop: 80}}>
                <Row>
                  <Col span={24}>
                    <div style={{textAlign: "center", fontSize: "28px"}}>
                      {i18next.t("activate:Account Activate")}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col span={24}>
                    {
                      !activateConfig ? null :
                        <Steps
                          current={getStepIndex(currentStepName, activateConfig)}
                          labelPlacement="vertical"
                          style={{
                            width: "90%",
                            maxWidth: "700px",
                            margin: "auto",
                            marginTop: "60px",
                          }}
                        >
                          {
                            activateConfig.flow.map(flowStep => {
                              return <Step key={flowStep.value}
                                title={Setting.activationProcess[flowStep.value].labelText}
                                icon={activationProcessIcon[flowStep.value]}
                              />;
                            })
                          }
                          <Step
                            title={i18next.t("activate:Complete")}
                            icon={<CheckCircleFilled />}
                          />
                        </Steps>
                    }
                  </Col>
                </Row>
              </Col>
              <Col span={24} style={{display: "flex", justifyContent: "center"}}>
                <div style={{marginTop: "10px", textAlign: "center"}}>
                  {
                    !activateConfig ? null
                      :
                      renderForm(applicationObj)
                  }
                </div>
              </Col>
            </Row>}
        </>
      )}
    </GlobalContext.Consumer>
  );
};

export default AccountActivatePage;
